import { CartLinesUpdateEvent } from '@shopify/events';

class SayeProductForm extends HTMLElement {
  connectedCallback() {
    // The gallery (main image, arrows, thumbnails) lives as a sibling of this
    // element within .saye-pm__inner, not nested inside it — scope those
    // lookups to the shared parent instead of `this`.
    this.root = this.closest('.saye-pm__inner') || this;

    this.variants = JSON.parse(this.querySelector('[data-variants-json]').textContent);
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.optionGroups = Array.from(this.querySelectorAll('[data-option-index]'));
    this.submitBtn = this.querySelector('[data-submit]');
    this.submitLabel = this.querySelector('[data-submit-label]');
    this.qtyInput = this.querySelector('[data-quantity]');
    this.qtyDecreaseBtn = this.querySelector('[data-qty-decrease]');
    this.qtyIncreaseBtn = this.querySelector('[data-qty-increase]');
    this.mediaEls = Array.from(this.root.querySelectorAll('.saye-pm__media-sources [data-media-id]'));
    this.thumbEls = Array.from(this.root.querySelectorAll('[data-thumb]'));
    this.mainWrap = this.root.querySelector('.saye-pm__main-wrap');
    this.prevBtn = this.root.querySelector('[data-pm-prev]');
    this.nextBtn = this.root.querySelector('[data-pm-next]');
    this.thumbsToggle = this.root.querySelector('[data-thumbs-toggle]');
    this.extraThumbs = Array.from(this.root.querySelectorAll('[data-extra-thumb]'));
    this.currentMediaIndex = 0;
    this.form = this.querySelector('form');

    this._setupReadMore();

    this.thumbEls.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this._goToMediaIndex(index));
    });

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._goToMediaIndex(this.currentMediaIndex - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._goToMediaIndex(this.currentMediaIndex + 1));
    if (this.thumbsToggle) this.thumbsToggle.addEventListener('click', () => this._toggleExtraThumbs());

    this._setupSwipe();

    this.selectedOptions = this.optionGroups.map((group) => {
      const selected = group.querySelector('.is-selected');
      return selected ? selected.dataset.optionValue : group.querySelector('[data-option-value]')?.dataset.optionValue;
    });

    this.optionGroups.forEach((group) => {
      group.addEventListener('click', (event) => {
        const button = event.target.closest('[data-option-value]');
        if (!button) return;
        const index = parseInt(group.dataset.optionIndex, 10);
        this._selectOption(group, button, index);
      });
    });

    if (this.qtyInput) {
      this.qtyInput.addEventListener('change', () => {
        if (this.qtyInput.value < 1) this.qtyInput.value = 1;
      });
    }

    if (this.qtyDecreaseBtn) this.qtyDecreaseBtn.addEventListener('click', () => this._stepQuantity(-1));
    if (this.qtyIncreaseBtn) this.qtyIncreaseBtn.addEventListener('click', () => this._stepQuantity(1));

    if (this.form) {
      this.form.addEventListener('submit', (event) => this._addToCart(event));
    }
  }

  _stepQuantity(delta) {
    if (!this.qtyInput) return;
    const current = parseInt(this.qtyInput.value, 10) || 1;
    this.qtyInput.value = Math.max(1, current + delta);
  }

  _selectOption(group, button, index) {
    Array.from(group.querySelectorAll('[data-option-value]')).forEach((el) => el.classList.remove('is-selected'));
    button.classList.add('is-selected');
    this.selectedOptions[index] = button.dataset.optionValue;
    const label = group.querySelector('[data-option-label]');
    if (label) label.textContent = button.dataset.optionValue;
    this._syncVariant();
  }

  _syncVariant() {
    const match = this.variants.find((variant) =>
      this.selectedOptions.every((value, i) => variant.options[i] === value)
    );
    if (!match) return;

    this.variantIdInput.value = match.id;
    // Price/compare-price text is set manually in the section settings, not
    // pulled from the variant, so switching options doesn't touch it here.
    if (this.submitBtn) this.submitBtn.disabled = !match.available;
    if (this.submitLabel) {
      this.submitLabel.textContent = match.available ? this.dataset.buyLabel : this.dataset.soldOutLabel;
    }
    if (match.featured_image && this.mediaEls.length) {
      const index = this.mediaEls.findIndex((el) => el.dataset.mediaId === String(match.featured_image.id));
      if (index > -1) this._goToMediaIndex(index);
    }
  }

  _showMedia(target) {
    const main = this.root.querySelector('[data-main-media]');
    if (!main) return;
    main.innerHTML = target.dataset.mediaHtml ? target.dataset.mediaHtml : target.innerHTML;
  }

  _goToMediaIndex(index) {
    if (!this.mediaEls.length) return;
    const count = this.mediaEls.length;
    this.currentMediaIndex = (index + count) % count;

    const target = this.mediaEls[this.currentMediaIndex];
    if (target) this._showMedia(target);

    this.thumbEls.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === this.currentMediaIndex);
    });

    const activeThumb = this.thumbEls[this.currentMediaIndex];
    if (activeThumb && activeThumb.hidden) this._toggleExtraThumbs(true);
  }

  _toggleExtraThumbs(forceExpand) {
    if (!this.thumbsToggle || !this.extraThumbs.length) return;
    const isExpanded = this.thumbsToggle.getAttribute('aria-expanded') === 'true';
    const expand = forceExpand || !isExpanded;

    this.extraThumbs.forEach((thumb) => {
      thumb.hidden = !expand;
    });
    this.thumbsToggle.setAttribute('aria-expanded', String(expand));
    this.thumbsToggle.classList.toggle('is-expanded', expand);
    const icon = this.thumbsToggle.querySelector('[data-thumbs-toggle-icon]');
    if (icon) icon.textContent = expand ? '▴' : '▾';
    this.thumbsToggle.setAttribute('aria-label', expand ? 'Show fewer photos' : 'Show all photos');
  }

  _setupSwipe() {
    if (!this.mainWrap) return;

    let startX = 0;
    let deltaX = 0;

    this.mainWrap.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
        deltaX = 0;
      },
      { passive: true }
    );

    this.mainWrap.addEventListener(
      'touchmove',
      (event) => {
        deltaX = event.touches[0].clientX - startX;
      },
      { passive: true }
    );

    this.mainWrap.addEventListener('touchend', () => {
      if (Math.abs(deltaX) > 40) {
        this._goToMediaIndex(this.currentMediaIndex + (deltaX < 0 ? 1 : -1));
      }
    });
  }

  _setupReadMore() {
    const desc = this.querySelector('[data-description-clamped]');
    const button = this.querySelector('[data-read-more]');
    if (!desc || !button) return;

    const moreLabel = button.dataset.readMoreLabel || button.textContent;
    const lessLabel = button.dataset.readLessLabel || moreLabel;

    requestAnimationFrame(() => {
      if (desc.scrollHeight > desc.clientHeight + 1) {
        button.hidden = false;
      }
    });

    button.addEventListener('click', () => {
      const isClamped = desc.hasAttribute('data-description-clamped');
      if (isClamped) {
        desc.removeAttribute('data-description-clamped');
        button.textContent = lessLabel;
      } else {
        desc.setAttribute('data-description-clamped', '');
        button.textContent = moreLabel;
      }
    });
  }

  async _addToCart(event) {
    event.preventDefault();
    if (this.submitBtn.disabled) return;

    const originalLabel = this.submitLabel.textContent;
    this.submitBtn.disabled = true;
    this.submitLabel.textContent = this.dataset.addingLabel;

    const quantity = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: this.variantIdInput.value, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.variantIdInput.value, quantity: quantity }] }),
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const cart = await fetch('/cart.js').then((r) => r.json());

      this.submitLabel.textContent = this.dataset.addedLabel;
      this._updateCartBadge(cart);
      this._openCartDrawer();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'saye-product-form' },
      });
    } catch (error) {
      this.submitLabel.textContent = this.dataset.errorLabel;
      deferred.reject(error);
    } finally {
      setTimeout(() => {
        this.submitLabel.textContent = originalLabel;
        this.submitBtn.disabled = false;
      }, 1600);
    }
  }

  _updateCartBadge(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  }

  _openCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }
}

customElements.define('saye-product-form', SayeProductForm);
